import _ from "lodash";
import { ReactElement } from "react";
import * as Yup from "yup";

import { createOrEditResponseScriptInputSchema } from "@stw/yup-validators";

import {
  EditResponseScriptInput,
  FieldsForResponseScriptFragment,
  FieldsForResponseScriptGroupFragment,
} from "~frontend/__generated__/graphql";
import { submitErrorField } from "~frontend/components/forms/FormikErrorBanner";
import { ResponseScriptEditForm } from "~frontend/components/responseScripts/ResponseScriptEditForm";
import { ListTagsTag } from "~frontend/graphql/tags";
import { useMediaUpload } from "~frontend/hooks/useMediaUpload";
import { CampaignForEditor } from "~frontend/routes/subaccounts/campaignEditor/types";
import { useErrorMessageFormatter } from "~frontend/util/errors";

import { ScriptContainer } from "../../routes/subaccounts/campaignEditor/InitialMessage";
import { addClientSideMediaUploadValidation } from "../../routes/subaccounts/campaignEditor/Script/ScriptVariants";
import { FormikDialog } from "../forms/FormikDialog";
import { MediaFieldValue } from "../forms/FormikMediaField";
import {
  ResponseScriptDialogContent,
  ResponseScriptDialogTitle,
} from "./ScriptEditor";

export default function EditScriptDialog({
  open,
  subaccountTags,
  scriptGroups,
  valuesToEdit,
  campaign,
  onClose,
  onSubmit,
}: {
  open: boolean;
  subaccountTags: ListTagsTag[];
  scriptGroups: FieldsForResponseScriptGroupFragment[];
  valuesToEdit: FieldsForResponseScriptFragment;
  campaign?: CampaignForEditor;
  onClose: () => void;
  onSubmit: (values: EditResponseScriptInput) => Promise<void>;
}): ReactElement {
  const getErrorMessage = useErrorMessageFormatter();
  const { uploadMedia } = useMediaUpload();

  const groupsForScript = scriptGroups.filter(
    group => !!group.memberships.find(m => m.script.id === valuesToEdit.id)
  );

  const tags = subaccountTags.map(({ id, name }) => ({
    id,
    label: name,
  }));
  const groups = scriptGroups.map(({ id, title }) => ({
    id,
    label: title,
  }));
  return (
    <FormikDialog
      open={open}
      initialValues={{
        [submitErrorField]: null,
        scriptId: valuesToEdit.id,
        title: valuesToEdit.title,
        script: valuesToEdit.script,
        media: { upload: valuesToEdit.mediaUpload },
        groupIds: _.map(groupsForScript, "id"),
        tagIds: _.map(valuesToEdit.tags, "id"),
        keywordIdsToDelete: [],
        keywordsToAdd: [],
      }}
      validationSchema={addClientSideMediaUploadValidation(
        createOrEditResponseScriptInputSchema(Yup).fields.input
      )}
      onSubmit={async (values, formik) => {
        try {
          const mediaUpload = await uploadMedia(
            values.media as MediaFieldValue
          );
          await onSubmit({
            scriptId: values.scriptId,
            title: values.title,
            script: values.script,
            mediaUploadId: mediaUpload?.id,
            groupIds: values.groupIds,
            tagIds: values.tagIds,
            keywordIdsToDelete: values.keywordIdsToDelete,
            keywordsToAdd: values.keywordsToAdd,
          });
          formik.resetForm();
        } catch (e) {
          formik.setErrors({
            [submitErrorField]: getErrorMessage(e),
          });
        }
      }}
    >
      <ResponseScriptDialogTitle>
        Edit Script Response
      </ResponseScriptDialogTitle>
      <ResponseScriptDialogContent>
        <ScriptContainer>
          <ResponseScriptEditForm
            onClose={onClose}
            tags={tags}
            groups={groups}
            keywords={valuesToEdit.keywords}
            submitText="Update"
            loadingText="Updating..."
            campaign={campaign}
          />
        </ScriptContainer>
      </ResponseScriptDialogContent>
    </FormikDialog>
  );
}
